import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, X, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Botao discreto "Explique" que abre modal com explicacao gerada por IA.
// Props:
//   contexto   (string, obrigatorio) — descricao curta do dado, ex: "presenca em votacoes"
//   valor      (string, obrigatorio) — valor formatado, ex: "R$ 850.000" ou "87%"
//   tipo       (string, opcional)    — categoria: valor, percentual, data, contrato, patrimonio
//   fonte      (string, opcional)    — nome da fonte oficial, ex: "Câmara dos Deputados"
//   entidade   (string, opcional)    — nome do candidato/empresa/municipio
//   size       (sm|md, opcional)     — controla o tamanho do botao (default sm)
//   variant    (icon|pill, opcional) — visual (default pill)
//   testId     (string, opcional)    — data-testid override
const ExplainButton = ({
  contexto,
  valor,
  tipo = 'generico',
  fonte = 'Portal da Transparência',
  entidade = null,
  size = 'sm',
  variant = 'pill',
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchExplanation = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const token = localStorage.getItem('tdo_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(
        `${API}/public/ai/explain`,
        { contexto, valor, tipo, fonte, entidade },
        { headers, timeout: 25000, withCredentials: true },
      );
      setData(resp.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 429) {
        setError(detail || 'Limite de explicações atingido. Volte em uma hora ou assine o Pro.');
      } else if (status === 503) {
        setError('Serviço temporariamente indisponível. Tente novamente em instantes.');
      } else {
        setError(detail || 'Não foi possível gerar a explicação agora.');
      }
    } finally {
      setLoading(false);
    }
  }, [contexto, valor, tipo, fonte, entidade]);

  const handleOpen = () => {
    setOpen(true);
    fetchExplanation();
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setData(null);
  };

  const btnClasses = cn(
    'inline-flex items-center gap-1 rounded-full font-medium transition-colors',
    'text-[#006030] bg-[#E8F5E9] hover:bg-[#C8E6C9]',
    'focus:outline-none focus:ring-2 focus:ring-[#006030]/30',
    variant === 'icon'
      ? 'p-1'
      : size === 'md'
        ? 'text-xs px-2.5 py-1'
        : 'text-[11px] px-2 py-0.5',
  );

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={btnClasses}
        data-testid={testId || 'explain-btn'}
        aria-label={`Explique: ${contexto}`}
        title="Explicar este dado com IA"
      >
        <Sparkles className={variant === 'icon' ? 'w-3.5 h-3.5' : 'w-3 h-3'} />
        {variant !== 'icon' && <span>Explique</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="explain-modal-title"
          data-testid="explain-modal"
        >
          <div
            className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-[#E2E8F0]">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-[#006030]" />
                </div>
                <div className="min-w-0">
                  <h3
                    id="explain-modal-title"
                    className="text-sm font-bold text-[#0F1C2E]"
                  >
                    Explicação com IA
                  </h3>
                  <p className="text-[11px] text-[#94A3B8] mt-0.5 truncate">
                    {contexto}: <span className="font-semibold text-[#475569]">{valor}</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-1 -mr-1 rounded-md text-[#475569] hover:bg-[#F4F6F8]"
                data-testid="explain-close-btn"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 min-h-[110px]">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-[#475569]" data-testid="explain-loading">
                  <Loader2 className="w-4 h-4 animate-spin text-[#006030]" />
                  <span>Consultando a IA...</span>
                </div>
              )}

              {error && !loading && (
                <div
                  className="flex items-start gap-2 text-sm text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded p-3"
                  data-testid="explain-error"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {data && !loading && !error && (
                <div data-testid="explain-content">
                  <p className="text-sm text-[#0F1C2E] leading-relaxed">
                    {data.explicacao}
                  </p>
                  {data.cached && (
                    <p className="mt-3 text-[10px] text-[#94A3B8] italic">
                      Explicação de cache. Renovada automaticamente a cada 30 dias.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-[#F9FAFB] border-t border-[#E2E8F0] flex items-center justify-between">
              <span className="text-[10px] text-[#94A3B8]">Gerado por IA. Sem opinião política.</span>
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-1 text-xs font-semibold text-[#006030] hover:bg-[#E8F5E9] rounded-md"
                data-testid="explain-ok-btn"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExplainButton;
